"use client";

import Link from "next/link";
import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Knopf } from "../knopf";
import { SchliessenSymbol, useEingehaengt } from "./kleines-modal";
import { useModalVerhalten } from "./modal-verhalten";

/*
 * Detail-Modal D1 (Musterkatalog M-008, Design-Guide `detail-formen.md`): ein Objekt aus Liste oder Brett öffnen und kurz
 * handeln. Zweispaltig über die Slots `haupt` (links: Beschreibung, Listen, Aktivität) und `seite` (rechts: Handlung und
 * Eigenschaften, eigene Scrollspalte = „sticky“). Kopf: Pfad, optional ↑↓ mit „n von m“, „Als Seite öffnen“, Schließen.
 * Unter `lg` Vollfläche mit einer Scrollspalte (Hauptteil, dann Seitenteil).
 *
 * Abweichung AW-008 vom Template (`projects.html`: `lg:max-w-4xl`): Breite `max-w-6xl`, ab `2xl` `max-w-7xl`, mobil
 * Vollfläche, Hintergrund mit Blur, React-gesteuert statt `hs-overlay` (Betreiber 07.10.2026, M-008).
 *
 * Verhalten (per Test belegt): `role="dialog"`, `aria-modal`, `aria-labelledby`, Esc schließt, Fokusfalle, Fokus kehrt zum
 * Auslöser zurück, Klick auf den Hintergrund schließt, j/k blättern (nicht in Eingabefeldern).
 */

export interface DetailPfadGlied {
  label: string;
  href?: string;
}

export interface DetailBlaettern {
  /** Nullbasiert. */
  index: number;
  gesamt: number;
  onVor: () => void;
  onZurueck: () => void;
  /** Beschriftung der Knöpfe, z. B. „Meilenstein“ ergibt „Vorheriger Meilenstein“. */
  objektArt: string;
}

// Scrollspalten: feste, vollständige Ketten (R8), Scrollbar-Familie wie im Original (erlaubte-sonderwerte.json).
const SCROLL_GESAMT =
  "grow min-h-0 flex flex-col lg:flex-row overflow-y-auto lg:overflow-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb";
const SCROLL_HAUPT =
  "grow min-w-0 lg:overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb";
// Seitenspalte vor dem Hauptteil unter `lg` (Handlung zuerst, M-011): `order-first` nur unter `lg`, Trennlinie darunter statt darüber.
const SCROLL_SEITE_ZUERST =
  "max-lg:order-first lg:w-96 lg:shrink-0 border-b lg:border-b-0 lg:border-s border-overlay-header bg-background-1 lg:overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb";
// Hauptspalte mit festem Fuß (Composer): die Spalte hält Höhe, der Hauptteil darüber scrollt (ab `lg`); darunter eine Spalte.
const HAUPT_MIT_FUSS = "grow min-w-0 lg:min-h-0 flex flex-col lg:overflow-hidden";
const SCROLL_HAUPT_INNEN =
  "grow lg:min-h-0 lg:overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb";
const SCROLL_SEITE =
  "lg:w-96 lg:shrink-0 border-t lg:border-t-0 lg:border-s border-overlay-header bg-background-1 lg:overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb";

// Form `voll` (AW-008) und `kompakt` (Template-Breite, Höhe nach Inhalt: die Hülle hält die Höhe des Templates, die Karte oben darin ist nur so hoch wie ihr Inhalt, höchstens die Hülle, darüber scrollt der Inhalt).
const HUELLE_VOLL =
  "mt-0 lg:mt-7 opacity-100 lg:max-w-6xl 2xl:max-w-7xl lg:w-full m-0 lg:m-3 lg:mx-auto h-full lg:h-[calc(100%-56px)] lg:min-h-[calc(100%-56px)] flex items-center";
const KARTE_VOLL = "size-full max-h-full flex flex-col bg-overlay border border-overlay-line lg:rounded-xl overflow-hidden pointer-events-auto shadow-xl";
const HUELLE_KOMPAKT = "mt-0 lg:mt-7 opacity-100 lg:max-w-4xl lg:w-full m-0 lg:m-3 lg:mx-auto h-full lg:h-[calc(100%-56px)] flex items-start";
const KARTE_KOMPAKT = "w-full max-h-full flex flex-col max-lg:h-full bg-overlay border border-overlay-line lg:rounded-xl overflow-hidden pointer-events-auto shadow-xl";
const SCROLL_KOMPAKT =
  "grow min-h-0 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb";

function Pfeil({ richtung }: { richtung: "auf" | "ab" }) {
  return (
    <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {richtung === "auf" ? (
        <>
          <path d="m5 12 7-7 7 7" />
          <path d="M12 19V5" />
        </>
      ) : (
        <>
          <path d="M12 5v14" />
          <path d="m19 12-7 7-7-7" />
        </>
      )}
    </svg>
  );
}

function ExternSymbol() {
  return (
    <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

function TrennSymbol() {
  return (
    <svg className="shrink-0 mx-1 size-3.5 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

export function DetailModal(props: {
  offen: boolean;
  /** Überschrift des Objekts (benennt den Dialog). */
  titel: string;
  /** Kleine Zeile über dem Titel (z. B. Kennung „MS 4“). */
  kennung?: string;
  /** Chips unter dem Titel (Status, Zustand), höchstens eine Pille je Achse (M-001); bei `form="kompakt"` in derselben Zeile rechts neben dem Titel. */
  chips?: ReactNode;
  pfad: DetailPfadGlied[];
  /** Ziel von „Als Seite öffnen“ (D1: jede Detail-Ansicht hat es). */
  seitenHref: string;
  blaettern?: DetailBlaettern;
  beimSchliessen: () => void;
  haupt: ReactNode;
  /** Seitenteil rechts; genau `null` = keine Seitenspalte, der Hauptteil nimmt die volle Breite (z. B. Hinweis-Zustände). */
  seite: ReactNode;
  /** Additiv (M-011): bearbeitbarer Titel statt Text; `titel` benennt den Dialog weiter (für Hilfstechnik). */
  titelInhalt?: ReactNode;
  /** Additiv (M-011): Aktionen im Kopf vor „Als Seite öffnen“ (z. B. Mehr-Menü). */
  kopfAktionen?: ReactNode;
  /** Additiv (M-011): fester Fuß der Hauptspalte (z. B. Composer); der Hauptteil scrollt darüber. */
  hauptFuss?: ReactNode;
  /** Additiv (M-011): unter `lg` steht die Seitenspalte (Handlung zuerst) vor dem Hauptteil. */
  seiteZuerst?: boolean;
  /**
   * Additiv (CMDB-Kurzansicht, Betreiber 03.11.2026 P-C 9 „A“): `kompakt` = Template-Breite `lg:max-w-4xl`, Höhe nach Inhalt (ab `lg`),
   * einspaltig (nur `haupt`, `seite` wird ignoriert); Standard `voll` unverändert (AW-008).
   */
  form?: "voll" | "kompakt";
  /** Additiv: graue Zeile unter Titel und Chips (z. B. Kurzbeschreibung), nur mit Inhalt übergeben. */
  untertitel?: ReactNode;
  /** Additiv: fester Fuß unter dem Inhalt über die ganze Breite (z. B. Meta-Zeile „Geändert · Angelegt“). */
  fuss?: ReactNode;
}) {
  const eingehaengt = useEingehaengt();
  if (!props.offen || !eingehaengt) return null;
  return createPortal(<Inhalt {...props} />, document.body);
}

function istEingabe(ziel: EventTarget | null): boolean {
  if (!(ziel instanceof HTMLElement)) return false;
  return ziel.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(ziel.tagName);
}

function Inhalt({
  titel,
  kennung,
  chips,
  pfad,
  seitenHref,
  blaettern,
  beimSchliessen,
  haupt,
  seite,
  titelInhalt,
  kopfAktionen,
  hauptFuss,
  seiteZuerst,
  form = "voll",
  untertitel,
  fuss,
}: {
  titel: string;
  kennung?: string;
  chips?: ReactNode;
  pfad: DetailPfadGlied[];
  seitenHref: string;
  blaettern?: DetailBlaettern;
  beimSchliessen: () => void;
  haupt: ReactNode;
  seite: ReactNode;
  titelInhalt?: ReactNode;
  kopfAktionen?: ReactNode;
  hauptFuss?: ReactNode;
  seiteZuerst?: boolean;
  form?: "voll" | "kompakt";
  untertitel?: ReactNode;
  fuss?: ReactNode;
}) {
  const kompakt = form === "kompakt";
  const huelle = useRef<HTMLDivElement>(null);
  const id = useId();
  useModalVerhalten({ offen: true, huelle, beimSchliessen });

  // j/k blättern (Entscheid D1), nicht während man in ein Feld tippt und nicht mit Strg/Alt/Meta.
  const blaetternRef = useRef(blaettern);
  useEffect(() => {
    blaetternRef.current = blaettern;
  }, [blaettern]);
  useEffect(() => {
    const beiTaste = (e: KeyboardEvent) => {
      const b = blaetternRef.current;
      if (!b || e.ctrlKey || e.altKey || e.metaKey || istEingabe(e.target)) return;
      if (!huelle.current?.contains(document.activeElement)) return;
      if (e.key === "j" && b.index < b.gesamt - 1) b.onVor();
      else if (e.key === "k" && b.index > 0) b.onZurueck();
    };
    document.addEventListener("keydown", beiTaste);
    return () => document.removeEventListener("keydown", beiTaste);
  }, []);

  return (
    <>
      {/* Preline: productivity/work-management/projects.html#project-details */}
      {/* Preline: dashboards/admin-dashboard/kanban-board.html#detail-overlay */}
      <div className="fixed inset-0 z-70 bg-foreground/50 backdrop-blur-md" aria-hidden="true" onClick={beimSchliessen} />
      <div
        ref={huelle}
        className="fixed top-0 inset-s-0 size-full z-80 overflow-hidden pointer-events-none"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-titel`}
        tabIndex={-1}
      >
        <div className={kompakt ? HUELLE_KOMPAKT : HUELLE_VOLL}>
          <div className={kompakt ? KARTE_KOMPAKT : KARTE_VOLL}>
            <div data-modal-kopf className="shrink-0 border-b border-overlay-header">
              <div className="py-2.5 ps-4 pe-2 sm:ps-6 flex items-center justify-between gap-x-2">
                <nav aria-label="Brotkrumen" className="min-w-0">
                  <ol className="flex items-center whitespace-nowrap text-xs text-muted-foreground-1">
                    {pfad.map((g, i) => (
                      <li key={`${g.label}-${i}`} className="min-w-0 flex items-center">
                        {i > 0 && <TrennSymbol />}
                        {g.href ? (
                          <Link href={g.href} className="truncate hover:underline">
                            {g.label}
                          </Link>
                        ) : (
                          <span className="truncate" aria-current={i === pfad.length - 1 ? "page" : undefined}>
                            {g.label}
                          </span>
                        )}
                      </li>
                    ))}
                  </ol>
                </nav>
                <div className="flex items-center gap-x-1 shrink-0">
                  {blaettern && (
                    <div className="flex items-center gap-x-0.5">
                      {/* Betreiber 10.10.2026, Preline-Maß: Ghost-Symbolknopf 34 px, rounded-lg */}
                      <Knopf
                        variante="ghost"
                        nurSymbol
                        groesse="sm"
                        label={`Vorheriger ${blaettern.objektArt}`}
                        titel={`Vorheriger ${blaettern.objektArt} (k)`}
                        symbol={<Pfeil richtung="auf" />}
                        onClick={blaettern.onZurueck}
                        disabled={blaettern.index <= 0}
                      />
                      <Knopf
                        variante="ghost"
                        nurSymbol
                        groesse="sm"
                        label={`Nächster ${blaettern.objektArt}`}
                        titel={`Nächster ${blaettern.objektArt} (j)`}
                        symbol={<Pfeil richtung="ab" />}
                        onClick={blaettern.onVor}
                        disabled={blaettern.index >= blaettern.gesamt - 1}
                      />
                      <span className="ms-1 me-2 hidden md:inline-flex items-center gap-x-1 text-xs text-muted-foreground-1" aria-live="polite">
                        {blaettern.index + 1} von {blaettern.gesamt}
                      </span>
                    </div>
                  )}
                  {kopfAktionen}
                  <Knopf href={seitenHref} sichtbar="ab-md" symbol={<ExternSymbol />}>
                    Als Seite öffnen
                  </Knopf>
                  <Knopf nurSymbol href={seitenHref} variante="ghost" groesse="sm" sichtbar="bis-md" label="Als Seite öffnen" titel="Als Seite öffnen" symbol={<ExternSymbol />} />
                  <Knopf variante="ghost" nurSymbol groesse="sm" label="Schließen (Esc)" symbol={<SchliessenSymbol />} onClick={beimSchliessen} />
                </div>
              </div>
              <div className="pt-1 pb-3 px-4 sm:px-6">
                {kennung && <span className="block text-xs font-medium text-muted-foreground-1 tracking-wide">{kennung}</span>}
                {titelInhalt ? (
                  <>
                    {/* Anfangsfokus auf der Überschrift (kein Eingabefeld): j/k blättern sofort, nichts wird versehentlich getippt. */}
                    <h2 id={`${id}-titel`} className="sr-only" tabIndex={-1} data-modal-fokus>
                      {titel}
                    </h2>
                    <div className="mt-0.5">{titelInhalt}</div>
                  </>
                ) : kompakt && chips ? (
                  /* Kurzansicht (Betreiber 03.11.2026, P-C 9): Typ und Status in derselben Zeile rechts neben dem Namen, Umbruch bei Platzmangel (wie `SeitenTitelzeile` mit `titelZusatz` im Detail-Kopf). */
                  <div data-modal-titelzeile className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <h2 id={`${id}-titel`} className="text-lg sm:text-xl font-semibold text-foreground">
                      {titel}
                    </h2>
                    {chips}
                  </div>
                ) : (
                  <h2 id={`${id}-titel`} className="mt-0.5 text-lg sm:text-xl font-semibold text-foreground">
                    {titel}
                  </h2>
                )}
                {chips && !(kompakt && !titelInhalt) && <div className="mt-1.5 flex flex-wrap items-center gap-2">{chips}</div>}
                {untertitel && <div className="mt-2 text-sm text-muted-foreground-1">{untertitel}</div>}
              </div>
            </div>

            {kompakt ? (
              <>
                <div className={SCROLL_KOMPAKT}>
                  <div className="p-4 sm:p-6 space-y-4">{haupt}</div>
                </div>
                {fuss && (
                  <div data-modal-fuss className="shrink-0 border-t border-overlay-header px-4 sm:px-6 py-3">
                    {fuss}
                  </div>
                )}
              </>
            ) : (
            <div className={SCROLL_GESAMT}>
              {hauptFuss ? (
                <div className={HAUPT_MIT_FUSS}>
                  <div className={SCROLL_HAUPT_INNEN}>
                    <div className="p-4 sm:p-6 space-y-6">{haupt}</div>
                  </div>
                  <div data-modal-fuss className="shrink-0 border-t border-overlay-header bg-overlay">{hauptFuss}</div>
                </div>
              ) : (
                <div className={SCROLL_HAUPT}>
                  <div className="p-4 sm:p-6 space-y-6">{haupt}</div>
                </div>
              )}
              {/* Betreiber 10.10.2026: ohne Seitenteil (`seite={null}`) entfällt die Spalte, der Hauptteil nimmt die volle Breite. */}
              {seite !== null && (
                <aside aria-label="Eigenschaften" className={seiteZuerst ? SCROLL_SEITE_ZUERST : SCROLL_SEITE}>
                  <div className="p-4 sm:p-5 space-y-4">{seite}</div>
                </aside>
              )}
            </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
